import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { hittingLine, pitchingLine, fmtDate, cnt, rate3, inningsFromOuts } from "./statFormat";
import { CalendarDays, MapPin, PlayCircle, ChevronRight, ClipboardList } from "lucide-react";

const PAGE = 10;

const OutcomePill = ({ o }) => {
  if (!o) return null;
  const map = { W: "bg-emerald-600", L: "bg-[#E63946]", T: "bg-slate-500" };
  return <span className={`inline-block text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${map[o] || "bg-slate-500"}`}>{o}</span>;
};

const homeAwayLabel = { home: "vs", away: "@", neutral: "vs" };

function GameCard({ g }) {
  const h = g.hitting || {};
  const p = g.pitching || {};
  const hl = hittingLine(h);
  const pl = pitchingLine(p);
  const fielding = (g.fielding || []).filter((f) => cnt(f.total_chances) > 0);
  const scoreStr = g.us != null && g.them != null ? `${g.us}–${g.them}` : null;
  return (
    <div data-testid={`gamelog-row-${g.game_id}`} className="border border-slate-200 rounded-sm p-4 hover:border-[#0A192F] transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <CalendarDays className="w-3.5 h-3.5" /> {fmtDate(g.date)}
            {g.game_type && <span className="uppercase tracking-wider text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{g.game_type}</span>}
            {g.is_player_only && <span data-testid={`gamelog-player-only-${g.game_id}`} className="uppercase tracking-wider text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm font-bold">Verified Player Performance</span>}
          </div>
          <div className="mt-1 font-semibold text-[#0A192F] flex items-center gap-2 flex-wrap">
            {!g.is_player_only && <OutcomePill o={g.outcome} />}
            <span>{homeAwayLabel[g.home_away] || "vs"} {g.opponent_team_slug ? (
              <Link to={`/live/teams/${g.opponent_team_slug}`} className="hover:text-[#E63946]">{g.opponent_name || "Opponent"}</Link>
            ) : (g.opponent_name || "Opponent")}</span>
            {scoreStr && !g.is_player_only && <span className="font-data text-slate-500">{scoreStr}</span>}
          </div>
          {g.is_player_only && <div className="text-[11px] text-amber-600 mt-0.5">Team result not recorded — individual performance only</div>}
          {g.tournament_name && <div className="text-xs text-slate-500 mt-0.5">{g.tournament_name}</div>}
          <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {g.venue && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.venue}</span>}
            {g.positions_played?.length > 0 && <span>Pos: {g.positions_played.join(", ")}</span>}
          </div>
        </div>
        <Link to={`/games/${g.game_id}`} data-testid={`gamelog-detail-${g.game_id}`} aria-label="View full game detail" className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946] hover:underline">
          Box <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {hl && <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Hitting</span><span className="font-data">{hl}</span></div>}
        {pl && <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Pitching</span><span className="font-data">{pl}</span></div>}
        {!hl && !pl && fielding.length > 0 && (
          <div><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">Fielding</span>
            <span className="font-data">{fielding.map((f) => `${f.position} ${cnt(f.po)}PO/${cnt(f.a)}A`).join(", ")}</span></div>
        )}
      </div>

      {g.public_recap && <p className="mt-2 text-xs text-slate-600 italic">"{g.public_recap}"</p>}

      {(g.video_url || g.highlight_url) && (
        <a href={g.video_url || g.highlight_url} target="_blank" rel="noreferrer" data-testid={`gamelog-video-${g.game_id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A192F] hover:text-[#E63946]">
          <PlayCircle className="w-4 h-4" /> Watch Highlight
        </a>
      )}
    </div>
  );
}

export default function PlayerGameLogTab({ playerId, filters }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ tournament_id: "", opponent: "", game_type: "", date_from: "", date_to: "", context: "" });

  const load = useCallback((reset) => {
    setLoading(true);
    const off = reset ? 0 : offset;
    const params = { limit: PAGE, offset: off };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get(`/players/${playerId}/game-log`, { params })
      .then((r) => {
        setTotal(r.data?.total || 0);
        setItems((prev) => (reset ? r.data.items : [...prev, ...r.data.items]));
        setOffset(off + PAGE);
      })
      .catch(() => { if (reset) setItems([]); })
      .finally(() => setLoading(false));
  }, [playerId, offset, f]);

  useEffect(() => { setOffset(0); load(true); /* eslint-disable-next-line */ }, [playerId, f]);

  if (!filters?.has_approved_data) {
    return (
      <div data-testid="gamelog-tab" className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">
        <ClipboardList className="w-6 h-6 mx-auto text-slate-300 mb-2" />
        No approved games yet. Verified game logs will appear here as CBR approves box scores.
      </div>
    );
  }

  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div data-testid="gamelog-tab" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="gamelog-filter-context" aria-label="Filter by team context" value={f.context} onChange={(e) => setFilter("context", e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white">
          <option value="">All Teams</option>
          <option value="cbr_nation">CBR Nation</option>
          <option value="regular_team">Regular Team</option>
        </select>
        {filters.tournaments?.length > 1 && (
          <select data-testid="gamelog-filter-tournament" aria-label="Filter by tournament" value={f.tournament_id} onChange={(e) => setFilter("tournament_id", e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white">
            <option value="">All Tournaments</option>
            {filters.tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {filters.opponents?.length > 1 && (
          <select data-testid="gamelog-filter-opponent" aria-label="Filter by opponent" value={f.opponent} onChange={(e) => setFilter("opponent", e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white">
            <option value="">All Opponents</option>
            {filters.opponents.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {filters.game_types?.length > 1 && (
          <select data-testid="gamelog-filter-gametype" aria-label="Filter by game type" value={f.game_type} onChange={(e) => setFilter("game_type", e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white">
            <option value="">All Types</option>
            {filters.game_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">From
          <input type="date" data-testid="gamelog-filter-from" value={f.date_from} onChange={(e) => setFilter("date_from", e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white" />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">To
          <input type="date" data-testid="gamelog-filter-to" value={f.date_to} onChange={(e) => setFilter("date_to", e.target.value)} className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white" />
        </label>
      </div>

      {items.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500">No games match these filters.</div>
      ) : (
        <div className="space-y-3">
          {items.map((g) => <GameCard key={g.game_id} g={g} />)}
        </div>
      )}

      {items.length < total && (
        <button data-testid="gamelog-load-more" onClick={() => load(false)} disabled={loading} className="w-full py-3 border border-slate-300 rounded-sm text-sm font-semibold uppercase tracking-wider text-[#0A192F] hover:border-[#0A192F] disabled:opacity-50">
          {loading ? "Loading…" : `Load More (${total - items.length} remaining)`}
        </button>
      )}
    </div>
  );
}
